import React from 'react';
import PropTypes from 'prop-types';
import List from './list';

function normalizeItems(items, nameField, descriptionField, valueField) {
  return items.map(item => {
    return {
      name: item[nameField],
      description: descriptionField ? item[descriptionField] : undefined,
      value: item[valueField],
    };
  });
}

export default function SelectionList(props) {
  const {nameField, descriptionField, valueField, initialSelectedValue, items, onChange, ...rest} = props;
  const [selectedValue, setSelectedValue] = React.useState(initialSelectedValue);
  const [searchTerm, setSearchTerm] = React.useState('');

  const normalizedItems = React.useMemo(() => {
    return normalizeItems(items, nameField, descriptionField, valueField);
  }, [items, nameField, descriptionField, valueField]);

  const filteredItems = React.useMemo(() => {
    const filtered = normalizedItems.filter(item => {
      const normalizedSearchTerm = searchTerm.toLowerCase();
      const keep =
        !searchTerm ||
        item.name.toLowerCase().indexOf(normalizedSearchTerm) >= 0 ||
        item.description?.toLowerCase().indexOf(normalizedSearchTerm) >= 0;
      if (item.value === selectedValue && !keep) {
        setSelectedValue(undefined);
        onChange(undefined);
      }
      return keep;
    });
    return filtered;
  }, [normalizedItems, searchTerm]);

  const onSelectionChanged = React.useCallback(newSelectedValue => {
    if (newSelectedValue !== selectedValue) {
      const selectedItem = items.find(item => item[valueField] === newSelectedValue);
      setSelectedValue(newSelectedValue);
      onChange(selectedItem);
    }
  });

  return (
    <List
      items={filteredItems}
      showDescriptions={!!descriptionField}
      selectedValue={selectedValue}
      onChange={onSelectionChanged}
      onSearch={setSearchTerm}
      {...rest}
    />
  );
}

SelectionList.defaultProps = {
  searchable: true,
  searchPlaceholder: 'search',
  nameField: 'name',
  descriptionField: 'description',
  valueField: 'value',
};

SelectionList.propTypes = {
  /** Whether or not a search box should be show for the list. */
  searchable: PropTypes.bool,
  /** Optional i18n key to override the placeholder text shown in the search box. */
  searchPlaceholder: PropTypes.string,
  /** A text description to show above the list. If not specified, no label will be shown. */
  label: PropTypes.string,
  /** The field on a data object that should be used as the name of the selection item. */
  nameField: PropTypes.string,
  /** The field on a data object that should be used as the description of the selection item. If set to null, no description will be shown. */
  descriptionField: PropTypes.string,
  /** The field on a data object that should be used as the item's value. This is what will be returned when this item is selected. */
  valueField: PropTypes.string,
  /** The currently selected value. If set, then this will be compared to the valueField of each item to determine which is selected. This is only used to set the initial selection. */
  initialSelectedValue: PropTypes.any,
  /** An array of items to render. */
  items: PropTypes.array,
  /** A callback to execute when an item is selected. Will pass undefined if selection is cleared. */
  onChange: PropTypes.func,
};
